import { useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import '../AllCss/FilterSidebar.css';

// Preferred ordering/catalog of makes. Counts will be filled from inventory; absent => 0
export const MAKES_MODELS  = [
  'Acura','Alfa Romeo','Aston Martin','Audi','Bentley','BMW','Buick','Cadillac','Chevrolet','Chrysler','Dodge','Ferrari','FIAT','Ford','Genesis','GMC','Honda','Hyundai','Infiniti','Jaguar','Jeep','Kia','Lamborghini','Land Rover','Lexus','Lincoln','Lucid','Maserati','Mazda','McLaren','Mercedes-Benz','Mini','Mitsubishi','Nissan','Polestar','Porsche','Ram','Range Rover','Rivian','Rolls-Royce','Scion','Smart','Subaru','Tesla','Toyota','Volkswagen','Volvo'
];

// Fuel type catalog with canonical key and aliases present in inventory
const FUEL_TYPES = [
  { label: 'Petrol', key: 'gasoline', aliases: ['gas'] },
  { label: 'Diesel', key: 'diesel', aliases: [] },
  { label: 'Electric', key: 'electric', aliases: [] },
  { label: 'Hybrid', key: 'hybrid', aliases: [] },
  { label: 'Plug-In Hybrid', key: 'plug-in hybrid', aliases: ['plug-in-hybrid', 'plugin hybrid', 'phev'] },
];

// Seats catalog to show common seat counts
const SEAT_OPTIONS = [2, 4, 5, 6, 7, 8];

// Exterior/Interior color catalogs
const EXTERIOR_COLORS = [
  { label: 'Black', key: 'black' },
  { label: 'Blue', key: 'blue' },
  { label: 'Brown', key: 'brown' },
  { label: 'Gold', key: 'gold' },
  { label: 'Gray', key: 'gray', aliases: ['grey'] },
  { label: 'Green', key: 'green' },
  { label: 'Orange', key: 'orange' },
  { label: 'Purple', key: 'purple' },
  { label: 'Red', key: 'red' },
  { label: 'Silver', key: 'silver' },
  { label: 'Tan', key: 'tan' },
  { label: 'White', key: 'white' },
  { label: 'Yellow', key: 'yellow' },
];

const INTERIOR_COLORS = EXTERIOR_COLORS;

// Drivetrain catalog
const DRIVETRAINS = [
  { label: 'All Wheel Drive', key: 'awd', aliases: ['all wheel drive', 'all-wheel drive'] },
  { label: 'Four Wheel Drive', key: '4wd', aliases: ['four wheel drive', '4 wheel drive', '4x4'] },
  { label: 'Front Wheel Drive', key: 'fwd', aliases: ['front wheel drive'] },
  { label: 'Rear Wheel Drive', key: 'rwd', aliases: ['rear wheel drive'] },
];

// Body types catalog
export const BODY_TYPES = [
  { label: 'Sedan', key: 'sedan' },
  { label: 'SUV', key: 'suv', aliases: ['crossover'] },
  { label: 'Hatchback', key: 'hatchback' },
  { label: 'Coupe', key: 'coupe' },
  { label: 'Convertible', key: 'convertible' },
  { label: 'Wagon', key: 'wagon' },
  { label: 'Minivan', key: 'minivan', aliases: ['van'] },
  { label: 'Truck', key: 'truck', aliases: ['pickup'] },
  { label: 'Other', key: 'other' },
];

// Extra features catalog grouped
const FEATURE_GROUPS = [
  { title: 'Most Popular', items: [
    { label: 'Apple CarPlay', key: 'apple carplay' },
    { label: 'Heated Seats', key: 'heated seats' },
    { label: 'Genuine Leather Seats', key: 'genuine leather seats' },
    { label: 'Synthetic Leather Seats', key: 'synthetic leather seats' },
    { label: 'Sunroof', key: 'sunroof' },
    { label: 'Third Row Seat', key: 'third row seat' },
  ]},
  { title: 'Braking & Traction', items: [
    { label: 'Adjustable Suspension', key: 'adjustable suspension' },
    { label: 'Auto Leveling Suspension', key: 'auto leveling suspension' },
    { label: 'Electronic Stability Control', key: 'electronic stability control' },
    { label: 'Hill Assistance', key: 'hill assistance' },
    { label: 'Intelligent Traction Control', key: 'intelligent traction control' },
    { label: 'Premium Brakes', key: 'premium brakes' },
    { label: 'Rollover Mitigation', key: 'rollover mitigation' },
    { label: 'Upgraded Suspension', key: 'upgraded suspension' },
  ]},
  { title: 'Cargo & Towing', items: [
    { label: 'Roof Rails', key: 'roof rails' },
    { label: 'Tow Hitch', key: 'tow hitch' },
    { label: 'Tow Hitch and Connector', key: 'tow hitch and connector' },
    { label: 'Trailer Brake Controller', key: 'trailer brake controller' },
    { label: 'Truck Bed Cover', key: 'truck bed cover' },
    { label: 'Truck Bed Liner', key: 'truck bed liner' },
  ]},
  { title: 'Comfort & Convenience', items: [
    { label: '115V Power Outlet', key: '115v power outlet' },
    { label: 'Automatic Windshield Wipers', key: 'automatic windshield wipers' },
    { label: 'Hands Free Trunk Access', key: 'hands free trunk access' },
    { label: 'Heated Mirrors', key: 'heated mirrors' },
    { label: 'Heated Steering Wheel', key: 'heated steering wheel' },
    { label: 'Heated Windshield', key: 'heated windshield' },
    { label: 'Integrated Garage Door Opener', key: 'integrated garage door opener' },
    { label: 'Keyless Entry', key: 'keyless entry' },
    { label: 'Memory Exterior Mirrors', key: 'memory exterior mirrors' },
    { label: 'Multi-Zone Climate Control', key: 'multi-zone climate control' },
    { label: 'Power Adjustable Pedals', key: 'power adjustable pedals' },
    { label: 'Power Adjustable Steering Column', key: 'power adjustable steering column' },
    { label: 'Power Folding Exterior Mirrors', key: 'power folding exterior mirrors' },
    { label: 'Power Lumbar Support', key: 'power lumbar support' },
    { label: 'Power Mirrors', key: 'power mirrors' },
    { label: 'Power Sliding Doors', key: 'power sliding doors' },
    { label: 'Power Tailgate/Hatch', key: 'power tailgate/hatch' },
    { label: 'Push Button Start', key: 'push button start' },
    { label: 'Rear Climate Control', key: 'rear climate control' },
    { label: 'Rear Window Shades', key: 'rear window shades' },
    { label: 'Refrigerated Center Console', key: 'refrigerated center console' },
    { label: 'Refrigerator/Cooler', key: 'refrigerator/cooler' },
    { label: 'Remote Start', key: 'remote start' },
    { label: 'Self Driving', key: 'self driving' },
    { label: 'Soft Close Doors', key: 'soft close doors' },
    { label: 'Windshield Wiper De-Icer', key: 'windshield wiper de-icer' },
  ]},
  { title: 'Entertainment & Technology', items: [
    { label: 'Amazon Alexa', key: 'amazon alexa' },
    { label: 'Android Auto', key: 'android auto' },
    { label: 'Apple CarPlay', key: 'apple carplay' },
    { label: 'Bluetooth Music', key: 'bluetooth music' },
    { label: 'GPS Navigation', key: 'gps navigation' },
    { label: 'Hands Free Calling', key: 'hands free calling' },
    { label: 'Home Link', key: 'home link' },
    { label: 'Noise Cancellation', key: 'noise cancellation' },
    { label: 'Performance Monitoring', key: 'performance monitoring' },
    { label: 'Premium Sound System', key: 'premium sound system' },
    { label: 'Rear Seat Entertainment', key: 'rear seat entertainment' },
    { label: 'Satellite Radio Available', key: 'satellite radio available' },
    { label: 'Siri Eyes Free', key: 'siri eyes free' },
    { label: 'USB Input', key: 'usb input' },
    { label: 'Virtual Cockpit', key: 'virtual cockpit' },
    { label: 'Wi-Fi Hot Spot', key: 'wi-fi hot spot' },
  ]},
  { title: 'Exterior', items: [
    { label: 'Running Boards', key: 'running boards' },
  ]},
  { title: 'Lighting', items: [
    { label: 'Adaptive Headlights', key: 'adaptive headlights' },
    { label: 'Automatic High Beams', key: 'automatic high beams' },
    { label: 'Daytime Running Lights', key: 'daytime running lights' },
    { label: 'Fog Lights', key: 'fog lights' },
    { label: 'HID/Xenon Headlamps', key: 'hid/xenon headlamps' },
    { label: 'LED Daytime Running Lights', key: 'led daytime running lights' },
    { label: 'LED Headlamps', key: 'led headlamps' },
    { label: 'LED Tail Lights', key: 'led tail lights' },
  ]},
  { title: 'Performance & Drive', items: [
    { label: 'Dual-Clutch Automatic Transmission', key: 'dual-clutch automatic transmission' },
    { label: 'Paddle Shifters', key: 'paddle shifters' },
    { label: 'Performance Exhaust System', key: 'performance exhaust system' },
    { label: 'Rev-Matching Transmission', key: 'rev-matching transmission' },
  ]},
  { title: 'Roof & Glass', items: [
    { label: 'Hard Top', key: 'hard top' },
    { label: 'Power Retractable Hard Top', key: 'power retractable hard top' },
    { label: 'Power Retractable Soft Top', key: 'power retractable soft top' },
    { label: 'Sunroof', key: 'sunroof' },
    { label: 'Panoramic Sunroof', key: 'panoramic sunroof' },
    { label: 'Skylight', key: 'skylight' },
  ]},
  { title: 'Safety & Security', items: [
    { label: '360 Camera', key: '360 camera' },
    { label: 'Active Lane Keep Assistance', key: 'active lane keep assistance' },
    { label: 'Adaptive Cruise Control', key: 'adaptive cruise control' },
    { label: 'Auto Dimming Mirrors', key: 'auto dimming mirrors' },
    { label: 'Automatic Emergency Braking', key: 'automatic emergency braking' },
    { label: 'Automatic Self Parking', key: 'automatic self parking' },
    { label: 'Blindspot Sensors', key: 'blindspot sensors' },
    { label: 'Collision Detection', key: 'collision detection' },
    { label: 'Digital Rearview Mirror', key: 'digital rearview mirror' },
    { label: 'Front View Camera', key: 'front view camera' },
    { label: 'Heads Up Display', key: 'heads up display' },
    { label: 'Lane Departure Warning', key: 'lane departure warning' },
    { label: 'Night Vision', key: 'night vision' },
    { label: 'Parking Assistance', key: 'parking assistance' },
    { label: 'Pedestrian Detection', key: 'pedestrian detection' },
    { label: 'Rear Cross Traffic Alert', key: 'rear cross traffic alert' },
    { label: 'Rear View Camera', key: 'rear view camera' },
    { label: 'Safe Exit Assist', key: 'safe exit assist' },
    { label: 'Side View Cameras', key: 'side view cameras' },
    { label: 'Stolen Vehicle Tracking Software', key: 'stolen vehicle tracking software' },
    { label: 'Teen Driver Tech', key: 'teen driver tech' },
    { label: 'Traffic Sign Recognition', key: 'traffic sign recognition' },
  ]},
  { title: 'Seating & Interior', items: [
    { label: '2nd Row Captain\'s Chairs', key: '2nd row captain\'s chairs' },
    { label: 'Dual Power Rear Seats', key: 'dual power rear seats' },
    { label: 'Executive Rear Seating', key: 'executive rear seating' },
    { label: 'Genuine Leather Seats', key: 'genuine leather seats' },
    { label: 'Heated and Ventilated Seats', key: 'heated and ventilated seats' },
    { label: 'Heated Seats', key: 'heated seats' },
    { label: 'Leather Interior Trim', key: 'leather interior trim' },
    { label: 'Leather-Wrapped Steering Wheel', key: 'leather-wrapped steering wheel' },
    { label: 'Lounge Rear Seat', key: 'lounge rear seat' },
    { label: 'Multi-Row Seating', key: 'multi-row seating' },
    { label: 'Power Driver Seat', key: 'power driver seat' },
    { label: 'Power Folding Rear Seat', key: 'power folding rear seat' },
    { label: 'Power Folding Third Row Seat', key: 'power folding third row seat' },
    { label: 'Power Passenger Seat', key: 'power passenger seat' },
    { label: 'Rear Heated Seats', key: 'rear heated seats' },
    { label: 'Reclining Rear Seats', key: 'reclining rear seats' },
    { label: 'Seat Massage System', key: 'seat massage system' },
    { label: 'Seat Memory System', key: 'seat memory system' },
    { label: 'Synthetic Leather Seats', key: 'synthetic leather seats' },
    { label: 'Third Row Seat', key: 'third row seat' },
  ]},
  { title: 'Wheels & Tires', items: [
    { label: 'Alloy Wheels', key: 'alloy wheels' },
    { label: 'Painted Brake Calipers', key: 'painted brake calipers' },
  ]},
];

function Section({ title, children, defaultOpen = true, sectionRef, expandUp = false }) {
  return (
    <details className={`FilterSection ${expandUp ? 'expand-up' : ''}`} open={defaultOpen} ref={sectionRef}>
      <summary>{title}</summary>
      <div className="FilterSection-body">{children}</div>
    </details>
  );
}

function FiltersSidebar({ filters, onChange, onClear, options = {}, className = '', onClose }) {
  const sidebarRef = useRef(null);

  const scrollExpandedIntoView = useCallback((detailsEl) => {
    const sidebar = sidebarRef.current;
    if (!sidebar || !detailsEl?.open) return;
    requestAnimationFrame(() => {
      const padding = 12;
      const sidebarRect = sidebar.getBoundingClientRect();
      const targetRect = detailsEl.getBoundingClientRect();
      if (targetRect.bottom > sidebarRect.bottom - padding) {
        sidebar.scrollTop += targetRect.bottom - sidebarRect.bottom + padding;
      } else if (targetRect.top < sidebarRect.top + padding) {
        sidebar.scrollTop += targetRect.top - sidebarRect.top - padding;
      }
    });
  }, []);

  const handleDetailsToggle = useCallback(
    (e) => {
      const details = e.target;
      if (details.tagName !== 'DETAILS' || !details.open) return;
      if (!sidebarRef.current?.contains(details)) return;
      scrollExpandedIntoView(details);
    },
    [scrollExpandedIntoView]
  );

  /** When a capped list hits top/bottom, continue scrolling the full filter sidebar */
  const handleNestedListWheel = useCallback((e) => {
    const list = e.currentTarget;
    const sidebar = sidebarRef.current;
    if (!sidebar || !list) return;

    const { deltaY } = e;
    if (deltaY === 0) return;

    const atTop = list.scrollTop <= 0;
    const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;

    if ((deltaY > 0 && atBottom) || (deltaY < 0 && atTop)) {
      sidebar.scrollTop += deltaY;
      e.preventDefault();
    }
  }, []);

  function handle(e) {
    const { name, value } = e.target;
    onChange({ [name]: value });
  }
  
  function toggleMake(makeName) {
    const current = new Set(filters.selectedMakes || []);
    if (current.has(makeName)) {
      current.delete(makeName);
    } else {
      current.add(makeName);
    }
    onChange({ selectedMakes: Array.from(current) });
  }
  
  function toggleFuel(fuelKey) {
    const current = new Set(filters.selectedFuels || []);
    if (current.has(fuelKey)) {
      current.delete(fuelKey);
    } else {
      current.add(fuelKey);
    }
    onChange({ selectedFuels: Array.from(current) });
  }

  function formatCount(n) {
    const num = Number(n);
    return Number.isFinite(num) ? num.toLocaleString() : String(n ?? 0);
  }

  function toggleModel(modelName) {
    const current = new Set(filters.selectedModels || []);
    if (current.has(modelName)) {
      current.delete(modelName);
    } else {
      current.add(modelName);
    }
    onChange({ selectedModels: Array.from(current) });
  }

  function toggleExterior(colorKey) {
    const current = new Set(filters.selectedExteriorColors || []);
    if (current.has(colorKey)) current.delete(colorKey); else current.add(colorKey);
    onChange({ selectedExteriorColors: Array.from(current) });
  }

  function toggleInterior(colorKey) {
    const current = new Set(filters.selectedInteriorColors || []);
    if (current.has(colorKey)) current.delete(colorKey); else current.add(colorKey);
    onChange({ selectedInteriorColors: Array.from(current) });
  }

  function toggleDrivetrain(dtKey) {
    const current = new Set(filters.selectedDrivetrains || []);
    if (current.has(dtKey)) current.delete(dtKey); else current.add(dtKey);
    onChange({ selectedDrivetrains: Array.from(current) });
  }

  function toggleFeature(featKey) {
    const current = new Set(filters.selectedFeatures || []);
    if (current.has(featKey)) current.delete(featKey); else current.add(featKey);
    onChange({ selectedFeatures: Array.from(current) });
  }

  function toggleBodyType(btKey) {
    const current = new Set(filters.selectedBodyTypes || []);
    if (current.has(btKey)) current.delete(btKey); else current.add(btKey);
    onChange({ selectedBodyTypes: Array.from(current) });
  }

  return (
    <aside
      ref={sidebarRef}
      className={`FiltersSidebar ${className}`}
      onToggle={handleDetailsToggle}
    >
      <div className="FiltersSidebar-header">
        <h3>Filters</h3>
        <div className="FiltersSidebar-header-actions">
        <button className="link" type="button" onClick={onClear}>Clear all</button>
          {onClose && (
            <button 
              className="mobile-filter-close-btn" 
              type="button" 
              onClick={onClose}
              aria-label="Close filters"
            >
              <X size={20} strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>

      <Section title="Sort by">
        <select name="sortBy" value={filters.sortBy} onChange={handle}>
          <option value="best">Best match</option>
          <option value="priceAsc">Price: Low to High</option>
          <option value="priceDesc">Price: High to Low</option>
          <option value="priceDrop">Price Drop (Biggest Savings)</option>
          <option value="yearDesc">Year: Newest</option>
          <option value="mileageAsc">Mileage: Low to High</option>
        </select>
      </Section>

      <Section title="VIN Lookup">
        <input
          name="vin"
          type="text"
          placeholder="Enter VIN (full or partial)"
          value={filters.vin || ''}
          onChange={handle}
          autoComplete="off"
        />
      </Section>

      <Section title="Price">
        <div className="row">
          <label className="filter-currency-field">
            <span className="filter-currency-prefix" aria-hidden="true">$</span>
            <input
              name="priceMin"
              type="number"
              className="filter-currency-input"
              placeholder="Min"
              value={filters.priceMin}
              onChange={handle}
              min={0}
              step={1}
            />
          </label>
          <label className="filter-currency-field">
            <span className="filter-currency-prefix" aria-hidden="true">$</span>
            <input
              name="priceMax"
              type="number"
              className="filter-currency-input"
              placeholder="Max"
              value={filters.priceMax}
              onChange={handle}
              min={0}
              step={1}
            />
          </label>
        </div>
      </Section>

      <Section title="Make & Model" defaultOpen={false}>
        <div
          className="FilterChecklist FilterChecklist-make-model filter-panel-scroll"
          onWheel={handleNestedListWheel}
        >
          {(options.makesWithCounts || []).map((m) => {
            const checked = (filters.selectedMakes || []).includes(m.name);
            const models = m.count === 0 ? [] : ((options.modelsByMakeWithCounts && options.modelsByMakeWithCounts[m.name]) || []);
            return (
              <details key={m.name} className="FilterMakeGroup">
                <summary>
                  <label className="FilterChecklist-item">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMake(m.name)}
                    />
                    <span>{m.name} ({formatCount(m.count)})</span>
                  </label>
                </summary>
                <div className="FilterChecklist indent">
                  {models.map((model) => (
                    <label key={`${m.name}-${model.name}`} className="FilterChecklist-item">
                      <input
                        type="checkbox"
                        checked={(filters.selectedModels || []).includes(model.name)}
                        onChange={() => toggleModel(model.name)}
                      />
                      <span>{model.name} ({formatCount(model.count)})</span>
                    </label>
                  ))}
                </div>
              </details>
            );
          })}
        </div>
      </Section>

      <Section title="Body Type" defaultOpen={false}>
        <div className="FilterChecklist">
          {(options.bodyTypeWithCounts || []).map((b) => (
            <label key={`bt-${b.key}`} className="FilterChecklist-item">
              <input
                type="checkbox"
                checked={(filters.selectedBodyTypes || []).includes(b.key)}
                onChange={() => toggleBodyType(b.key)}
              />
              <span>{b.label} ({formatCount(b.count)})</span>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Year">
        <div className="row">
          <select name="yearMin" value={filters.yearMin} onChange={handle}>
            <option value="">From</option>
            {(options.years || []).map((y) => (
              <option key={`min-${y}`} value={y}>{y}</option>
            ))}
          </select>
          <select name="yearMax" value={filters.yearMax} onChange={handle}>
            <option value="">To</option>
            {(options.years || []).map((y) => (
              <option key={`max-${y}`} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </Section>

      <Section title="Mileage">
        <div className="row">
          <input name="mileageMin" type="number" placeholder="Min" value={filters.mileageMin} onChange={handle} />
          <input name="mileageMax" type="number" placeholder="Max" value={filters.mileageMax} onChange={handle} />
        </div>
      </Section>

      <Section title="Fuel Type" defaultOpen={false}>
        <div className="FilterChecklist">
          {(() => {
            const countsMap = new Map((options.fuelsWithCounts || []).map((f) => [f.key, f.count]));
            return FUEL_TYPES.map((f) => {
              const aliasSum = (f.aliases || []).reduce((acc, a) => acc + (countsMap.get(a) || 0), 0);
              const count = (countsMap.get(f.key) || 0) + aliasSum;
              const checked = (filters.selectedFuels || []).includes(f.key);
              return (
                <label key={f.key} className="FilterChecklist-item">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleFuel(f.key)}
                  />
                  <span>{f.label} ({formatCount(count)})</span>
                </label>
              );
            });
          })()}
        </div>
      </Section>

      <Section title="Seats" defaultOpen={false}>
        <div className="FilterChecklist">
          {(() => {
            const countsMap = new Map((options.seatsWithCounts || []).map((s) => [String(s.num), s.count]));
            return SEAT_OPTIONS.map((num) => ({ num, count: countsMap.get(String(num)) || 0 }));
          })().map((s) => {
            const checked = (filters.selectedSeats || []).includes(String(s.num));
            return (
              <label key={`seat-${s.num}`} className="FilterChecklist-item">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const current = new Set(filters.selectedSeats || []);
                    const key = String(s.num);
                    if (current.has(key)) current.delete(key); else current.add(key);
                    onChange({ selectedSeats: Array.from(current) });
                  }}
                />
                <span>{s.num} seats ({formatCount(s.count)})</span>
              </label>
            );
          })}
        </div>
      </Section>

      <Section title="Exterior Color" defaultOpen={false}>
        <div
          className="FilterChecklist filter-panel-scroll"
          onWheel={handleNestedListWheel}
        >
          {(() => {
            const countsMap = new Map((options.exteriorWithCounts || []).map((c) => [c.key, c.count]));
            return EXTERIOR_COLORS.map((c) => ({ ...c, count: (countsMap.get(c.key) || 0) + (c.aliases || []).reduce((acc, a) => acc + (countsMap.get(a) || 0), 0) }));
          })().map((c) => (
            <label key={`ext-${c.key}`} className="FilterChecklist-item">
              <input
                type="checkbox"
                checked={(filters.selectedExteriorColors || []).includes(c.key)}
                onChange={() => toggleExterior(c.key)}
              />
              <span>{c.label} ({formatCount(c.count)})</span>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Interior Color" defaultOpen={false}>
        <div
          className="FilterChecklist filter-panel-scroll"
          onWheel={handleNestedListWheel}
        >
          {(() => {
            const countsMap = new Map((options.interiorWithCounts || []).map((c) => [c.key, c.count]));
            return INTERIOR_COLORS.map((c) => ({ ...c, count: (countsMap.get(c.key) || 0) + (c.aliases || []).reduce((acc, a) => acc + (countsMap.get(a) || 0), 0) }));
          })().map((c) => (
            <label key={`int-${c.key}`} className="FilterChecklist-item">
              <input
                type="checkbox"
                checked={(filters.selectedInteriorColors || []).includes(c.key)}
                onChange={() => toggleInterior(c.key)}
              />
              <span>{c.label} ({formatCount(c.count)})</span>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Drivetrain" defaultOpen={false}>
        <div className="FilterChecklist">
          {(() => {
            const countsMap = new Map((options.drivetrainWithCounts || []).map((d) => [d.key, d.count]));
            return DRIVETRAINS.map((d) => ({ ...d, count: (countsMap.get(d.key) || 0) + (d.aliases || []).reduce((acc, a) => acc + (countsMap.get(a) || 0), 0) }));
          })().map((d) => (
            <label key={`dt-${d.key}`} className="FilterChecklist-item">
              <input
                type="checkbox"
                checked={(filters.selectedDrivetrains || []).includes(d.key)}
                onChange={() => toggleDrivetrain(d.key)}
              />
              <span>{d.label} ({formatCount(d.count)})</span>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Extra Features" defaultOpen={false}>
        <div
          className="features-expanded-list filter-panel-scroll"
          onWheel={handleNestedListWheel}
        >
          {FEATURE_GROUPS.map((group) => (
            <div key={group.title} className="feature-group">
              <div className="feature-group-title">{group.title}</div>
              <div className="feature-group-items">
                {(() => {
                  const countsMap = new Map((options.featuresWithCounts || []).map((f) => [f.key, f.count]));
                  return group.items.map((f) => ({ ...f, count: countsMap.get(f.key) || 0 }));
                })().map((f) => (
                  <label key={`feat-${f.key}`} className="FilterChecklist-item">
                    <input
                      type="checkbox"
                      checked={(filters.selectedFeatures || []).includes(f.key)}
                      onChange={() => toggleFeature(f.key)}
                    />
                    <span>{f.label} ({formatCount(f.count)})</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </aside>
  );
}

export default FiltersSidebar;